import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiDownload, FiFileText } from "react-icons/fi";
import Layout from "../../components/Layout";
import { downloadsData } from "./downloadsData";
import DownloadLeadForm from "./DownloadLeadForm";

const SITE_URL = "https://mbasolutions.co";

const DOWNLOADS_SEO = {
  title: "Free Downloads & Resources | MBA Solutions",
  description:
    "Access free ISO certification guides, HACCP food safety checklists, and corporate training brochures from MBA Solutions.",
  keywords:
    "ISO certification guide PDF, HACCP checklist download, corporate training brochure, free compliance resources, MBA Solutions downloads",
  canonical: `${SITE_URL}/downloads`,
};

const buildDownloadsJsonLd = () => [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Downloads & Free Resources",
    description: DOWNLOADS_SEO.description,
    url: DOWNLOADS_SEO.canonical,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Downloads",
        item: DOWNLOADS_SEO.canonical,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "MBA Solutions Free Downloads",
    numberOfItems: downloadsData.length,
    itemListElement: downloadsData.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.title,
      description: item.description,
    })),
  },
];

const Downloads = () => {
  const [selected, setSelected] = useState(null);

  return (
  <Layout
    title={DOWNLOADS_SEO.title}
    description={DOWNLOADS_SEO.description}
    keywords={DOWNLOADS_SEO.keywords}
    canonical={DOWNLOADS_SEO.canonical}
    jsonLd={buildDownloadsJsonLd()}
  >
    <section className="relative py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1498050108023-c5249f4df085"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-gray-50" />
      </div>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-20"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--bg-primary)] mb-3">
            <FiFileText size={16} /> Resources Library
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Downloads & Free Resources
          </h1>
          <p className="text-gray-600 text-base md:text-lg">
            Access useful guides, checklists, and brochures to support your
            certification journey, audits, and corporate training initiatives.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {downloadsData.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                whileHover={{ y: -12 }}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100 flex flex-col"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-4 left-4 text-xs font-semibold px-3 py-1 rounded-full bg-[var(--bg-primary)] text-white">
                    {item.fileType}
                  </span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <Icon size={14} className="text-[var(--bg-primary)]" />
                    {item.category}
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                    {item.title}
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                    {item.description}
                  </p>
                  {item.file ? (
                    <button
                      type="button"
                      onClick={() => setSelected(item)}
                      className="self-start inline-flex items-center gap-2 text-sm font-semibold text-[var(--bg-primary)] hover:gap-3 transition-all"
                    >
                      <FiDownload size={16} /> Download
                    </button>
                  ) : (
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--bg-primary)] hover:gap-3 transition-all"
                    >
                      <FiDownload size={16} /> Download
                    </Link>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 text-center"
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
          >
            View All Resources
          </Link>
        </motion.div>
      </div>
    </section>

    {selected && (
      <DownloadLeadForm
        key={selected.title}
        resource={selected}
        onClose={() => setSelected(null)}
      />
    )}
  </Layout>
  );
};

export default Downloads;
